import {
  OrderState,
  PostV1CompaniesCompanyIdOrdersDispatchJobCardPdfsData,
} from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';

import { Filters } from '~store/OrdersDispatchStore';
import prefixKeys from '~utils/prefixKeys';

type DispatchJobCardPdfsBody = NonNullable<
  PostV1CompaniesCompanyIdOrdersDispatchJobCardPdfsData['body']
>;
type DispatchSearchRequest = Pick<DispatchJobCardPdfsBody, 'filter' | 'search'>;
type DispatchOrderFilter = NonNullable<DispatchSearchRequest['filter']>;
type DispatchJobFilter = NonNullable<DispatchOrderFilter['job']>;

function buildOrderStatesFilter(filters: Filters): DispatchOrderFilter {
  // A dispatch status count spans every order state, so the tab's order states and external
  // scope would hide jobs the count includes.
  if (filters.dispatchStatuses?.length) {
    return {};
  }

  if (filters.external === true) {
    return { external: true };
  }

  const orderFilter: DispatchOrderFilter = {};

  // Orders tab sends filter[external]=false so the server drops received-pending-only orders
  // (those belong solely on the External tab). The All tab leaves filter[external] unset.
  if (filters.external === false) {
    orderFilter.external = false;
  }

  if (filters.orderStates?.length) {
    let states = filters.orderStates;

    if (filters.refinedOrderStates?.length) {
      states = filters.refinedOrderStates;
    }

    // When searching, always include cancelled so users can find cancelled orders
    if (filters.search?.trim().length && !states.includes(OrderState.CANCELED)) {
      states = [...states, OrderState.CANCELED];
    }

    orderFilter.states = states;
  }

  return orderFilter;
}

function buildJobFilter(filters: Filters, includeDates: boolean) {
  const jobFilter: DispatchJobFilter = {};

  if (filters.customerAccountIds?.length) {
    jobFilter.customer_account_ids = filters.customerAccountIds;
  }

  if (filters.dispatchStatuses?.length) {
    jobFilter.dispatch_statuses = filters.dispatchStatuses;
  }

  if (filters.driverIds?.length) {
    jobFilter.driver_ids = filters.driverIds;
  }

  if (filters.projectsExternalIds?.length) {
    jobFilter.external_ids = filters.projectsExternalIds;
  }

  if (filters.projectIds?.length) {
    jobFilter.project_ids = filters.projectIds;
  }

  if (filters.vendorAccountIds?.length) {
    jobFilter.vendor_account_ids = filters.vendorAccountIds;
  }

  if (filters.equipmentOwnerCompanyIds?.length) {
    jobFilter.equipment = { company_ids: filters.equipmentOwnerCompanyIds };
  }

  if (includeDates && filters.startDate?.length) {
    jobFilter.start_date = dayjs.tz(filters.startDate).startOf('day').toISOString();
  }

  if (includeDates && filters.endDate?.length) {
    jobFilter.end_date = dayjs.tz(filters.endDate).endOf('day').toISOString();
  }

  if (filters.jobStates?.length) {
    jobFilter.states = filters.jobStates;
  }

  if (filters.materialIds?.length) {
    jobFilter.material_ids = filters.materialIds;
  }

  if (filters.pickUpSites?.length) {
    jobFilter.pickup_site_ids = filters.pickUpSites;
  }

  if (filters.dropoffSites?.length) {
    jobFilter.dropoff_site_ids = filters.dropoffSites;
  }

  if (filters.salespersonIds?.length) {
    jobFilter.salesperson_ids = filters.salespersonIds;
  }

  if (filters.siteTypes?.length) {
    jobFilter.site_types = filters.siteTypes;
  }

  if (filters.foremen?.length) {
    jobFilter.foreman_ids = filters.foremen;
  }

  if (filters.equipmentIds?.length) {
    jobFilter.equipment_ids = filters.equipmentIds;
  }

  if (filters.collaboratorIds?.length) {
    jobFilter.collaborator_ids = filters.collaboratorIds;
  }

  if (filters.aiReviewStatuses?.length) {
    jobFilter.ai_review = { statuses: filters.aiReviewStatuses };
  }

  if (filters.prevailingWage?.length === 1) {
    jobFilter.prevailing_wage = filters.prevailingWage[0] === 'true';
  }

  if (filters.ticketPotentialDuplicate?.length) {
    jobFilter.ticket = {
      potential_duplicate: filters.ticketPotentialDuplicate[0] === 'true',
    };
  }

  return jobFilter;
}

// The dispatch filters and search as the nested Faceted-Dispatch-Search-Request body. The dispatch
// list flattens this into its query (toDispatchOrdersQueryParams) and the dispatch job card PDF
// sends it as-is, so both cover the same orders and jobs. `includeDates` gates the job start/end
// dates so the date-scoped "are there results if only the dates are dropped?" probe can reuse the
// exact same filter logic.
export default function buildDispatchSearchRequest(
  filters: Filters,
  { includeDates }: { includeDates: boolean },
) {
  const searchTerm = filters.search?.trim();
  const orderFilter = buildOrderStatesFilter(filters);

  if (filters.dispatchNumberIds?.length) {
    orderFilter.dispatch_number_ids = filters.dispatchNumberIds;
  }

  if (filters.requesterIds?.length) {
    orderFilter.requester_ids = filters.requesterIds;
  }

  if (filters.serviceClassIds?.length) {
    orderFilter.service_class_ids = filters.serviceClassIds;
  }

  if (filters.equipmentTypes?.length) {
    orderFilter.equipment_type_ids = filters.equipmentTypes;
  }

  if (filters.departmentIds?.length) {
    orderFilter.department_ids = filters.departmentIds;
  }

  if (filters.ticketTypes?.length === 1) {
    orderFilter.default_digital_ticket_view = filters.ticketTypes[0] === 'true';
  }

  if (filters.hasUnreadMessages === true) {
    orderFilter.has_unread_messages = true;
  }

  const request: DispatchSearchRequest = {
    filter: { ...orderFilter, job: buildJobFilter(filters, includeDates) },
    ...(searchTerm?.length ? { search: { job: { datagrid: searchTerm } } } : {}),
  };

  return request;
}

export function toDispatchOrdersQueryParams({ filter, search }: DispatchSearchRequest) {
  const { job, ...orderFilter } = filter ?? {};
  const {
    ai_review: aiReview,
    equipment,
    load,
    payable_invoice: payableInvoice,
    receivable_invoice: receivableInvoice,
    ticket,
    ...jobFilter
  } = job ?? {};

  return {
    ...prefixKeys('filter', orderFilter),
    ...prefixKeys('filter[job]', jobFilter),
    ...(aiReview ? prefixKeys('filter[job][ai_review]', aiReview) : {}),
    ...(equipment ? prefixKeys('filter[job][equipment]', equipment) : {}),
    ...(load ? prefixKeys('filter[job][load]', load) : {}),
    ...(payableInvoice ? prefixKeys('filter[job][payable_invoice]', payableInvoice) : {}),
    ...(receivableInvoice
      ? prefixKeys('filter[job][receivable_invoice]', receivableInvoice)
      : {}),
    ...(ticket ? prefixKeys('filter[job][ticket]', ticket) : {}),
    ...(search?.job ? prefixKeys('search[job]', search.job) : {}),
  };
}
